import Component from './Component';

export default class Collapsible extends Component {
  static settings = {
    isPersistent: false,
    hasTransition: false,
    control: '.js-collapse-control',
    target: '.js-collapse-target',
  };

  #control;
  #target;
  #isHiding;
  #handleControlClick;
  #handleFocusout;
  #handleKeydown;
  #handleOutsideClick;
  #handleTransitionEnd;

  constructor (element, options = {}) {
    super(element, {
      ...Collapsible.settings,
      ...options
    });

    this.#isHiding = false;

    this.#control = this.getNode(this.settings.control);
    this.#target = this.getNode(this.settings.target);

    if (!this.#control) {
      throw new Error(`Collpase error: A control with the '${ this.settings.control }' selector is missing.`);
    }

    if (!this.#target) {
      throw new Error(`Collpase error: A target with the '${ this.settings.target }' selector is missing.`);
    }

    if (!this.#control.getAttribute('id')) {
      throw new Error(`Collpase error: The control needs an id to work correctly.`, this.#control);
    }

    if (!this.#target.getAttribute('id')) {
      throw new Error(`Collpase error: The target needs an id to work correctly.`, this.#target);
    }

    this.#handleControlClick = this._handleControlClick.bind(this);
    this.#handleFocusout = this._handleFocusout.bind(this);
    this.#handleKeydown = this._handleKeydown.bind(this);
    this.#handleOutsideClick = this._handleOutsideClick.bind(this);
    this.#handleTransitionEnd = this._handleTransitionEnd.bind(this);
  }

  get control () {
    return this.#control;
  }

  get target () {
    return this.#target;
  }

  get expanded () {
    return this.#control.getAttribute('aria-expanded') === 'true';
  }

  set expanded (state) {
    this.#control.setAttribute('aria-expanded', `${state}`);
  }

  get isHiding () {
    return this.#isHiding;
  }

  _handleControlClick (e) {
    this.toggle();
  }

  _handleFocusout (e) {
    if (e.relatedTarget && !this.#target.contains(e.relatedTarget) && !this.#control.contains(e.relatedTarget)) {
      this.close(false);
    }
  }

  _handleKeydown (e) {
    // Close on escape
    if (this.expanded && e.keyCode === 27) {
      this.toggle();
    }
  }

  _handleOutsideClick (e) {
    if (!this.#target.contains(e.target) && !this.#control.contains(e.target)) {
      this.close(false);
    }
  }

  _handleTransitionEnd (e) {
    if (e.target === this.#target) {
      this.#target.removeEventListener('transitionend', this.#handleTransitionEnd);
      this.#target.classList.remove('is-hiding');
      this.#isHiding = false;
      this.close();
    }
  }

  open () {
    this.#control.setAttribute('aria-expanded', 'true');
    this.#target.removeAttribute('hidden');

    if (!this.settings.isPersistent) {
      document.addEventListener('focusout', this.#handleFocusout);
      document.addEventListener('click', this.#handleOutsideClick);
    }

    this.fire('open', this);
  }

  close (refocus = true) {
    if (!this.settings.isPersistent) {
      document.removeEventListener('focusout', this.#handleFocusout);
      document.removeEventListener('click', this.#handleOutsideClick);
    }

    this.#control.setAttribute('aria-expanded', 'false');
    this.#target.setAttribute('hidden', '');
    this.#target.classList.remove('is-hiding');

    if (refocus) {
      this.#control.focus();
    }

    this.fire('close', this);
  }

  hide () {
    this.#target.classList.add('is-hiding');
    this.#isHiding = true;
    this.#target.addEventListener('transitionend', this.#handleTransitionEnd);
  }

  toggle () {
    if (this.settings.hasTransition) {
      this.expanded ? this.hide() : this.open();
    } else {
      this.expanded ? this.close() : this.open();
    }
  }

  mount () {
    this.#target.setAttribute('aria-labelledby', this.#control.getAttribute('id'));
    this.#target.setAttribute('tabindex', '-1');
    this.#target.setAttribute('hidden', '');
    this.#control.setAttribute('aria-haspopup', 'true');
    this.#control.setAttribute('aria-expanded', 'false');
    this.#control.setAttribute('aria-controls', this.#target.getAttribute('id'));

    this.#control.addEventListener('click', this.#handleControlClick);
    this.element.addEventListener('keydown', this.#handleKeydown);

    super.mount();
  }

  unmount () {
    if (this.expanded) {
      this.close(false);
    }

    this.#control.removeEventListener('click', this.#handleControlClick);
    this.element.removeEventListener('keydown', this.#handleKeydown);

    this.#control.removeAttribute('aria-haspopup');
    this.#control.removeAttribute('aria-expanded');
    this.#control.removeAttribute('aria-controls');
    this.#target.removeAttribute('aria-labelledby');
    this.#target.removeAttribute('tabindex');
    this.#target.removeAttribute('hidden');

    super.unmount();
  }
}
